import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Building2, Lock, Eye, EyeOff } from 'lucide-react';

const ACCESS_PASSWORD = "@K3p4l4s3k0l4";

const Register = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessPassword, setAccessPassword] = useState('');
  const [showAccessPassword, setShowAccessPassword] = useState(false);
  const [registerData, setRegisterData] = useState({
    username: '',
    password: '',
    role: 'admin_pusat'
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [companyName, setCompanyName] = useState('Walet ERP');
  const [companyLogo, setCompanyLogo] = useState(null);

  // Fetch company settings
  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings/public');
        setCompanyName(response.data.company_name || 'Walet ERP');
        setCompanyLogo(response.data.company_logo || null);
        document.title = response.data.company_name || 'Walet ERP';
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    if (accessPassword === ACCESS_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Akses diberikan');
    } else {
      toast.error('Password akses salah');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/register', registerData);
      toast.success('Registrasi berhasil!');
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrasi gagal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0891b2 0%, #10b981 100%)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 backdrop-blur-md mb-4">
            {companyLogo ? (
              <img 
                src={companyLogo} 
                alt="Company Logo" 
                className="w-16 h-16 rounded-full object-contain"
              />
            ) : (
              <Building2 size={36} className="text-white" />
            )}
          </div>
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>{companyName}</h1>
        </div>

        {!isAuthenticated ? (
          // Access Password Gate
          <Card className="shadow-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock size={20} />
                Akses Terbatas
              </CardTitle>
              <CardDescription>Masukkan password akses untuk membuka halaman registrasi</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAccessSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="access-password">Password Akses</Label>
                  <div className="relative">
                    <Input
                      id="access-password"
                      type={showAccessPassword ? "text" : "password"}
                      value={accessPassword}
                      onChange={(e) => setAccessPassword(e.target.value)}
                      placeholder="Masukkan password akses"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowAccessPassword(!showAccessPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showAccessPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700">
                  Verifikasi
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-sm text-slate-500 hover:text-slate-700 underline"
                  >
                    Kembali ke Login
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : (
          // Registration Form
          <Card className="shadow-2xl">
            <CardHeader>
              <CardTitle>Registrasi User Baru</CardTitle>
              <CardDescription>Buat akun baru untuk mengakses sistem</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-username">Username</Label>
                  <Input
                    id="register-username"
                    type="text"
                    value={registerData.username}
                    onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="register-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="register-password"
                      type={showPassword ? "text" : "password"}
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-role">Role</Label>
                  <Select
                    value={registerData.role}
                    onValueChange={(value) => setRegisterData({ ...registerData, role: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin_pusat">Admin Pusat</SelectItem>
                      <SelectItem value="admin_cabang">Admin Cabang</SelectItem>
                      <SelectItem value="investor">Investor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
                  {loading ? 'Loading...' : 'Register'}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-sm text-slate-500 hover:text-slate-700 underline"
                  >
                    Sudah punya akun? Login
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Register;
